import gettext from '@sdk/lang/gettext';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import { BreakRecord } from '@sdk/automatedBreaks/types/BreakRecord';
import { BreakViolation } from '@sdk/automatedBreaks/types/BreakViolation';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import { AutomatedBreakType } from '@sdk/settings/types';
import type { ClockInTime } from '@sdk/timeClock/utils/getActualClockInTimes';

import {
  TakenBreaksSummary,
  TakenBreaksSummaryFields,
  MissedBreaksSummary,
  SummaryEntry,
  SummaryTuple,
} from '@web/timeClock/modals/rows/ActualRow/clockInSummary.types';
import {
  getMealBreakSummary,
  getRestBreakSummary,
  getMissedMealBreakSummary,
  getMissedRestBreakSummary,
  getMealBreakViolationsSummary,
  getRestBreakViolationsSummary,
  getWaivedMealBreakSummary,
  getWaivedRestBreakSummary,
} from '@web/timeClock/utils/getScheduledBreaksSummary';
import { getExcessiveMessage } from '@web/timeClock/utils/multipleBreaksViolationsToText';
import { MaybeBreakAttestations } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';

export const takenBreaksSummary = (
  breakRecords: BreakRecord[],
  breakAttestations?: MaybeBreakAttestations
): TakenBreaksSummary => {
  const summary: TakenBreaksSummary = {
    meal: {
      regular: 0,
      excessiveCount: 0,
      violatedCount: 0,
      violationReasons: {} as Record<BreakViolation, number>,
      attestedWaived: 0,
      attestedTaken: 0,
    },
    rest: {
      regular: 0,
      excessiveCount: 0,
      violatedCount: 0,
      violationReasons: {} as Record<BreakViolation, number>,
      attestedWaived: 0,
      attestedTaken: 0,
    },
  };

  // count all the regular and violated breaks, group them via meal and rest break types
  breakRecords.reduce((acc, { type, violations, isExcessive }) => {
    if (!type) {
      // ignore standard breaks
      return acc;
    }
    if (isExcessive) {
      acc[type].excessiveCount++;
    } else if (!violations.length) {
      acc[type].regular++;
    } else {
      acc[type].violatedCount++;
      violations.forEach((violation: BreakViolation) => {
        if (!acc[type].violationReasons[violation]) {
          acc[type].violationReasons[violation] = 1;
        } else {
          acc[type].violationReasons[violation]++;
        }
      });
    }
    return acc;
  }, summary);

  if (!breakAttestations?.length) {
    return summary;
  }

  // Count all the breaks that have been taken but are not visible in form (aka attested ones).
  return breakAttestations.reduce((acc, { breakType, option }) => {
    if (option === AttestationOption.waived) {
      acc[breakType].attestedWaived++;
    } else if (option === AttestationOption.taken) {
      acc[breakType].attestedTaken++;
    }

    return acc;
  }, summary);
};

/**
 * DANGEROUS: this method does mutate tuples passed in params.
 *
 * It's a helper method that can build the actual tuples - pair of break summary items.
 * We used pairs to represent rows in UI as each row can display max 2 break summaries.
 *
 * @param tuples
 * @param item
 */
const addToTuples = (tuples: Array<SummaryTuple | SummaryEntry[]>, item: SummaryEntry) => {
  const lastTupleIndex = tuples.length ? tuples.length - 1 : 0;
  const tuple = tuples[lastTupleIndex];

  if (!tuple || tuple.length === 2) {
    // length of two means we already filled the tuple and we need to create next one
    tuples.push([item]);
  } else {
    tuple.push(item);
  }
};

export const getRegularMealBreaks = ({ regular, attestedTaken }: TakenBreaksSummaryFields) => ({
  // attestedTaken are breaks that employee forgot to "click" but attested they indeed took them, hence those are regular breaks
  text: getMealBreakSummary(regular + attestedTaken),
  themeColor: ThemeColorType.gray,
});

export const getWaivedMealBreaks = ({ attestedWaived }) => ({
  text: getWaivedMealBreakSummary(attestedWaived),
  themeColor: ThemeColorType.gray,
});

export const getViolatedMealBreaks = ({ violatedCount, violationReasons }) => ({
  text: getMealBreakSummary(violatedCount),
  themeColor: ThemeColorType.danger,
  tipText: getMealBreakViolationsSummary(violationReasons),
});

export const getExcessiveMealBreaks = ({ excessiveCount }) => ({
  text: excessiveCount ? getExcessiveMessage(AutomatedBreakType.meal, excessiveCount) : '',
  themeColor: ThemeColorType.danger,
});

export const getExcessiveRestBreaks = ({ excessiveCount }) => ({
  text: excessiveCount ? getExcessiveMessage(AutomatedBreakType.rest, excessiveCount) : '',
  themeColor: ThemeColorType.danger,
});

export const getMissedMealBreaks = (missedMealBreaks: number, attestedTakenMealBreaks: number) => ({
  // Missed breaks are calculated with settings + planned duration of the shift,
  // we must adjust them by subtracting taken ones
  text: getMissedMealBreakSummary(Math.max(0, missedMealBreaks - attestedTakenMealBreaks)),
  themeColor: ThemeColorType.danger,
});

export const getRegularRestBreaks = ({ regular, attestedTaken }: TakenBreaksSummaryFields) => ({
  // attestedTaken are breaks that employee forgot to "click" but attested they indeed took them, hence those are regular breaks
  text: getRestBreakSummary(regular + attestedTaken),
  themeColor: ThemeColorType.gray,
});

export const getWaivedRestBreaks = ({ attestedWaived }) => ({
  text: getWaivedRestBreakSummary(attestedWaived),
  themeColor: ThemeColorType.gray,
});

export const getViolatedRestBreaks = ({ violatedCount, violationReasons }) => ({
  text: getRestBreakSummary(violatedCount),
  themeColor: ThemeColorType.danger,
  tipText: getRestBreakViolationsSummary(violationReasons),
});

export const getMissedRestBreaks = (missedRestBreaks: number, attestedTakenRestBreaks: number) => ({
  // Missed breaks are calculated with settings + planned duration of the shift,
  // we must adjust them by subtracting taken ones
  text: getMissedRestBreakSummary(Math.max(0, missedRestBreaks - attestedTakenRestBreaks)),
  themeColor: ThemeColorType.danger,
});

export const getSummariesTextToDisplay = (
  takenBreaksSummary: TakenBreaksSummary,
  missedBreaksSummary: MissedBreaksSummary
): SummaryTuple[] => {
  const summaries = [
    getRegularMealBreaks(takenBreaksSummary.meal),
    getWaivedMealBreaks(takenBreaksSummary.meal),
    getViolatedMealBreaks(takenBreaksSummary.meal),
    getExcessiveMealBreaks(takenBreaksSummary.meal),
    getRegularRestBreaks(takenBreaksSummary.rest),
    getWaivedRestBreaks(takenBreaksSummary.rest),
    getViolatedRestBreaks(takenBreaksSummary.rest),
    getExcessiveRestBreaks(takenBreaksSummary.rest),
    getMissedMealBreaks(
      missedBreaksSummary.meal,
      takenBreaksSummary.meal.attestedTaken + takenBreaksSummary.meal.attestedWaived
    ),
    getMissedRestBreaks(
      missedBreaksSummary.rest,
      takenBreaksSummary.rest.attestedTaken + takenBreaksSummary.rest.attestedWaived
    ),
  ];

  const summaryTuples = [];
  summaries.forEach((summary) => {
    if (summary.text) {
      addToTuples(summaryTuples, summary);
    }
  });

  return summaryTuples;
};

type TimeToDisplayLabelProps = {
  date: Date;
  usTimeFormat: boolean;
  isInProgress: boolean;
};
export const getStartTimeToDisplay = ({
  date,
  usTimeFormat,
  isInProgress,
}: TimeToDisplayLabelProps) =>
  isInProgress
    ? `${gettext('Started at')} ${formatTime(date, usTimeFormat)}`
    : formatTime(date, usTimeFormat);

export const getEndTimeToDisplay = ({
  date,
  usTimeFormat,
  isInProgress,
}: TimeToDisplayLabelProps) =>
  isInProgress ? gettext('In progress') : formatTime(date, usTimeFormat);

export const getTimesToDisplay = ({
  worldClockEnabled,
  usTimeFormat,
  clockInTime,
}: {
  worldClockEnabled: boolean;
  usTimeFormat: boolean;
  clockInTime: ClockInTime;
}) => {
  const { clockIn, clockInLocal, clockOut, clockOutLocal, isInProgress } = clockInTime;

  const clockInDate = worldClockEnabled ? clockInLocal : clockIn;
  const clockOutDate = worldClockEnabled ? clockOutLocal : clockOut;

  const startTimeToDisplay = getStartTimeToDisplay({
    date: clockInDate,
    isInProgress,
    usTimeFormat,
  });

  const endTimeToDisplay = getEndTimeToDisplay({
    date: clockOutDate,
    isInProgress,
    usTimeFormat,
  });

  return {
    startTimeToDisplay,
    endTimeToDisplay,
  };
};
